import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'

export type NotificationCategory =
  | 'mention'
  | 'application_batch'
  | 'scorecard_submitted'
  | 'interview_event'
  | 'offer_event'
  | 'posting_status'
  | 'daily_digest'

export type NotificationChannel = 'in_app' | 'email' | 'push'

export interface NotificationPreferences {
  user_id: string
  mention_in_app: boolean
  mention_email: boolean
  mention_push: boolean
  application_batch_in_app: boolean
  application_batch_email: boolean
  application_batch_push: boolean
  scorecard_submitted_in_app: boolean
  scorecard_submitted_email: boolean
  scorecard_submitted_push: boolean
  interview_event_in_app: boolean
  interview_event_email: boolean
  interview_event_push: boolean
  offer_event_in_app: boolean
  offer_event_email: boolean
  offer_event_push: boolean
  posting_status_in_app: boolean
  posting_status_email: boolean
  posting_status_push: boolean
  daily_digest_in_app: boolean
  daily_digest_email: boolean
  daily_digest_push: boolean
  quiet_hours_enabled: boolean
  quiet_hours_start: string | null
  quiet_hours_end: string | null
  quiet_hours_tz: string
  sound_on_mention: boolean
}

const DEFAULTS: Omit<NotificationPreferences, 'user_id'> = {
  mention_in_app: true,
  mention_email: true,
  mention_push: false,
  application_batch_in_app: true,
  application_batch_email: false,
  application_batch_push: false,
  scorecard_submitted_in_app: true,
  scorecard_submitted_email: false,
  scorecard_submitted_push: false,
  interview_event_in_app: true,
  interview_event_email: true,
  interview_event_push: false,
  offer_event_in_app: true,
  offer_event_email: true,
  offer_event_push: false,
  posting_status_in_app: true,
  posting_status_email: false,
  posting_status_push: false,
  daily_digest_in_app: false,
  daily_digest_email: false,
  daily_digest_push: false,
  quiet_hours_enabled: false,
  quiet_hours_start: '22:00',
  quiet_hours_end: '08:00',
  quiet_hours_tz:
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  sound_on_mention: true, // on by default (Allan, 2026-10-18)
}

const PREFS_EVENT = 'gio:notification-prefs'

export function useNotificationPreferences() {
  const { user } = useAuth()
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    setLoading(true)
    ;(async () => {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()
      if (cancelled) return
      if (error) {
        console.error('Failed to load notification prefs', error)
      }
      setPrefs(
        (data as NotificationPreferences | null) ?? {
          user_id: user.id,
          ...DEFAULTS,
        },
      )
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [user?.id])

  useEffect(() => {
    const onChange = (e: Event) => {
      const next = (e as CustomEvent<NotificationPreferences>).detail
      if (next?.user_id === user?.id) setPrefs(next)
    }
    window.addEventListener(PREFS_EVENT, onChange)
    return () => window.removeEventListener(PREFS_EVENT, onChange)
  }, [user?.id])

  const update = useCallback(
    async (patch: Partial<NotificationPreferences>) => {
      if (!user?.id || !prefs) return
      const next = { ...prefs, ...patch }
      setPrefs(next)
      // Other readers (the notification sound) pick the change up at once.
      window.dispatchEvent(new CustomEvent(PREFS_EVENT, { detail: next }))
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({ ...next, user_id: user.id }, { onConflict: 'user_id' })
      if (error) {
        toast.error('Could not save preference')
        console.error(error)
      }
    },
    [user?.id, prefs],
  )

  // Back-compat shim for older consumers expecting react-query-like shape.
  const save = {
    mutateAsync: (patch: Partial<NotificationPreferences>) => update(patch),
    isPending: false,
  }

  return { prefs, data: prefs, loading, update, save }
}

export type CategoryKey = NotificationCategory

export const PREFS_CATEGORIES: { key: CategoryKey; label: string; description: string }[] = [
  { key: 'mention',             label: 'Mentions',           description: 'When someone @mentions you in a candidate comment.' },
  { key: 'application_batch',   label: 'New applications',   description: 'Batched digest of new candidates applying to your jobs.' },
  { key: 'scorecard_submitted', label: 'Scorecard submitted', description: 'When a teammate submits a scorecard on a candidate.' },
  { key: 'interview_event',     label: 'Interview events',   description: 'Interview scheduled, rescheduled, or canceled.' },
  { key: 'offer_event',         label: 'Offer events',       description: 'Offer created, approval requested, approved, or signed.' },
  { key: 'posting_status',      label: 'Job posting status', description: 'Posting published, expired, or board sync issues.' },
  { key: 'daily_digest',        label: 'Daily digest',       description: 'Once-a-day summary of pipeline activity.' },
]
